/**
 * Formats a stored document number (which may be in legacy format like `PI/2026-27/047`)
 * into the master-data-configured format (e.g. `DT-PI-2026-27/0047`).
 *
 * Falls back to the stored number if it cannot be parsed.
 */

export function normalizeFiscalYear(value: string): string {
  const v = String(value ?? '').trim()
  const matchHyphen = v.match(/^(\d{4})\s*[-/]\s*(\d{2,4})$/)
  if (matchHyphen) {
    const start = Number(matchHyphen[1])
    const endStr = matchHyphen[2]
    if (endStr.length === 2) return `${start}-${endStr}`
    const end = Number(endStr)
    return end === start + 1 ? `${start}-${String(end).slice(-2)}` : `${matchHyphen[1]}-${matchHyphen[2]}`
  }
  const match8Digits = v.match(/^(\d{4})(\d{4})$/)
  if (match8Digits) {
    const start = Number(match8Digits[1])
    const end = Number(match8Digits[2])
    return end === start + 1 ? `${start}-${String(end).slice(-2)}` : `${match8Digits[1]}-${match8Digits[2]}`
  }
  return v
}

export function applyNumberFormat(
  storedNumber: string | undefined | null,
  options: {
    numberFormat?: string
    padding?: number
    fiscalYear?: string
    targetPrefix?: string
  } = {}
): string {
  if (!storedNumber) return ''

  const phaseMatch = String(storedNumber ?? '').match(/\s*\(([IVXLCDM\d]+)\)\s*$/i)
  const phaseSuffix = phaseMatch ? `(${phaseMatch[1]})` : ''
  const clean = String(storedNumber ?? '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim()
  if (!clean) return storedNumber

  const fmt = (options.numberFormat || 'DT-{PREFIX}-{FY}/{NUMBER}').trim()
  const pad = options.padding ?? 4
  const defaultFy = options.fiscalYear ? normalizeFiscalYear(options.fiscalYear) : ''

  let prefix = ''
  let fy = ''
  let seq = ''

  const match = clean.match(/^(?:DT[-/])?([A-Z/]+?)[-_/](\d{4}(?:[-/]?\d{2,4})?)[-_/](\d+)$/i)
  if (match) {
    prefix = match[1].toUpperCase()
    fy = match[2]
    seq = match[3]
  } else {
    return storedNumber
  }

  const finalPrefix = options.targetPrefix || prefix
  const paddedSeq = String(parseInt(seq, 10)).padStart(pad, '0')
  const normFy = normalizeFiscalYear(fy) || defaultFy

  let result = fmt
  if (result.includes('DT-{PREFIX}') && finalPrefix.startsWith('DT-')) {
    result = result.replace('DT-{PREFIX}', '{PREFIX}')
  }

  result = result.replace(/\{PREFIX\}/g, finalPrefix)
  result = result.replace(/\{FY\}/g, normFy)
  result = result.replace(/\{NUMBER\}/g, paddedSeq)

  return `${result}${phaseSuffix}`
}
